"use client";

import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Form, Card } from "react-bootstrap";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  Autocomplete,
} from "@react-google-maps/api";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ScaleLoader } from "react-spinners";
import { useDispatch, useSelector } from "react-redux";
import {
  addAddressThunk,
  deleteAddressThunk,
  getAddressListThunk,
  getUserDetailsThunk,
  updateAddressThunk,
} from "@/app/features/thunks/userThunk";
import Swal from "sweetalert2";

const containerStyle = {
  width: "100%",
  height: "400px",
};

const centerDefault = {
  lat: 30.7192,
  lng: 76.7049,
};

const AddressModal = ({ show, setShow }) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
    libraries: ["places"],
  });

  const dispatch = useDispatch();
  const user_id = useSelector((store) => store?.auth?.user?.user?.id);
  const token = useSelector((store) => store?.auth?.user?.accessToken);
  const addressList = useSelector((store) => store?.user?.addressList);

  useEffect(() => {
    if (show) getAddressList();
  }, [show]);

  const getAddressList = async () => {
    await dispatch(getAddressListThunk({ token, user_id }));
  };

  const [editingId, setEditingId] = useState(null);
  const [mode, setMode] = useState("list");
  const [mapCenter, setMapCenter] = useState(centerDefault);
  const [markerPosition, setMarkerPosition] = useState(centerDefault);
  const [isloading, setIsLoading] = useState(false);

  const autoCompleteRef = useRef(null);
  const inputRef = useRef(null);

  const validationSchema = Yup.object().shape({
    address: Yup.string().required("Address is required"),
    city: Yup.string().required("City is required"),
    state: Yup.string().required("State is required"),
    country: Yup.string().required("Country is required"),
    zipcode: Yup.string().required("Pin Code is required"),
  });

  const formik = useFormik({
    initialValues: {
      address: "",
      city: "",
      state: "",
      country: "",
      zipcode: "",
      latitude: centerDefault.lat || "",
      longitude: centerDefault.lng || "",
      searched_address: "",
      state_short_name: "",
      country_short_name: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      if (editingId) {
        values.id = editingId;
        await dispatch(updateAddressThunk({ token, data: values }));
      } else {
        await dispatch(addAddressThunk({ token, data: values }));
      }
      await dispatch(getAddressListThunk({ token, user_id }));
      await dispatch(getUserDetailsThunk({ token }));

      setTimeout(() => {
        setIsLoading(false);
        formik.resetForm();
        setEditingId(null);
        setMode("list");
      }, 1000);
    },
  });

  const reverseGeocode = (lat, lng) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results?.[0]) {
        const place = results?.[0];
        const components = place.address_components;

        const getComponent = (type) =>
          components.find((c) => c.types.includes(type))?.long_name || "";
        const getShortName = (type) =>
          components.find((c) => c.types.includes(type))?.short_name || "";

        formik.setValues({
          ...formik.values,
          address: place.formatted_address,
          city: getComponent("locality") || getComponent("sublocality") || "",
          state: getComponent("administrative_area_level_1"),
          country: getComponent("country"),
          zipcode: getComponent("postal_code"),
          latitude: lat,
          longitude: lng,
          searched_address: place.formatted_address,
          state_short_name: getShortName("administrative_area_level_1"),
          country_short_name: getShortName("country"),
        });
      }
    });
  };

  const onPlaceChanged = () => {
    if (autoCompleteRef.current !== null) {
      const place = autoCompleteRef.current.getPlace();
      if (!place.geometry) return;

      const location = place.geometry.location;
      const lat = location.lat();
      const lng = location.lng();

      setMapCenter({ lat, lng });
      setMarkerPosition({ lat, lng });

      reverseGeocode(lat, lng);
    }
  };

  const handleEdit = (addr) => {
    const lat = parseFloat(addr.latitude);
    const lng = parseFloat(addr.longitude);

    const position = {
      lat: lat || centerDefault.lat,
      lng: lng || centerDefault.lng,
    };

    formik.setValues({
      address: addr.address || "",
      city: addr?.city || "",
      state: addr?.state || "",
      country: addr?.country || "",
      zipcode: addr?.zipcode || "",
      latitude: lat,
      longitude: lng,
      searched_address: addr?.searched_address || "",
      state_short_name: addr?.state_short_name || "",
      country_short_name: addr?.country_short_name || "",
    });

    setMapCenter(position);
    setMarkerPosition(position);
    setEditingId(addr.id);
    setMode("form");
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you really want to delete this address?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await dispatch(deleteAddressThunk({ token, id }));
        await dispatch(getAddressListThunk({ token, user_id }));
        await dispatch(getUserDetailsThunk({ token }));

        Swal.fire({
          title: "Deleted!",
          text: "Address has been deleted.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        Swal.fire({
          title: "Error!",
          text: "Failed to delete the address.",
          icon: "error",
        });
      }
    }
  };

  if (loadError) return <div>Error loading Google Maps</div>;
  if (!isLoaded) return <div>Loading Google Maps...</div>;

  return (
    <Modal
      show={show}
      onHide={() => {
        setShow(false);
        setMode("list");
      }}
      centered
      size="lg"
      className="add-address-modal"
    >
      <Modal.Header className="border-0">
        {mode === "form" ? (
          <>
            <Button
              variant="link"
              onClick={() => {
                formik.resetForm();
                setMode("list");
              }}
              className="me-3 text-decoration-none text-dark"
            >
              <svg
                className="me-2"
                width="6"
                height="11"
                viewBox="0 0 6 11"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.99997 1.5C4.99997 1.5 1 4.44593 1 5.5C1 6.55413 5 9.5 5 9.5"
                  stroke="#525252"
                  strokeWidth="1.1875"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Back
            </Button>
            <Modal.Title className="ms-2 sign-in-head text-center fw-bold">
              {editingId ? "Edit Address" : "Add New Address"}
            </Modal.Title>
          </>
        ) : (
          <>
            <Modal.Title className="text-dark fw-bold">
              <h1 className="modal-title sign-in-head" id="signInLabel">
                📍 Manage Addresses
              </h1>
            </Modal.Title>
            <Button
              variant="close"
              onClick={() => {
                setShow(false);
                setMode("list");
              }}
            />
          </>
        )}
      </Modal.Header>

      <Modal.Body className="px-md-4 py-md-3">
        {mode === "list" ? (
          <>
            {addressList?.length === 0 && (
              <p className="text-muted">No addresses saved.</p>
            )}
            {addressList && addressList?.length > 0 ? (
              addressList?.map((addr) => (
                <Card
                  key={addr.id}
                  className="mb-3 border-0 rounded-3 address-card"
                >
                  <Card.Body className="d-block d-md-flex justify-content-md-between align-items-start add-address-body p-0">
                    <div>
                      <Card.Title className="mb-1 text-capitalize">
                        {addr.label}
                      </Card.Title>
                      <Card.Text className="text-muted mb-0">
                        {addr.address}
                      </Card.Text>
                    </div>
                    <div className="d-flex justify-content-center justify-content-md-between mt-3 mt-md-0">
                      <Button
                        size="sm"
                        onClick={() => handleEdit(addr)}
                        className="me-2 address-edit-button"
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(addr?.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              ))
            ) : (
              <div>Address not found</div>
            )}
            <div className="text-center mt-4">
              <Button
                className="add-address-btn w-100"
                size="lg"
                onClick={() => {
                  formik.resetForm();
                  setMode("form");
                }}
              >
                + Add New Address
              </Button>
            </div>
          </>
        ) : (
          <div className="container py-md-4">
            <h3 className="text-center mb-4">Select Your Location</h3>

            <div className="mb-3 position-relative" style={{ zIndex: 10000 }}>
              <Autocomplete
                onLoad={(ac) => (autoCompleteRef.current = ac)}
                onPlaceChanged={onPlaceChanged}
              >
                <input
                  type="text"
                  ref={inputRef}
                  name="searched_address"
                  placeholder="Search for your address"
                  className="form-control shadow-sm signin-input"
                  autoComplete="off"
                />
              </Autocomplete>
            </div>

            <div className="mb-4 rounded border overflow-hidden shadow-sm">
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={mapCenter}
                zoom={15}
                onClick={(e) => {
                  const lat = e.latLng.lat();
                  const lng = e.latLng.lng();
                  setMarkerPosition({ lat, lng });
                  reverseGeocode(lat, lng);
                }}
              >
                <Marker
                  position={markerPosition}
                  draggable={true}
                  onDragEnd={(e) => {
                    const lat = e.latLng.lat();
                    const lng = e.latLng.lng();
                    setMarkerPosition({ lat, lng });
                    reverseGeocode(lat, lng);
                  }}
                />
              </GoogleMap>
            </div>

            <form onSubmit={formik.handleSubmit}>
              <div className="card shadow-sm p-2 p-md-4 mb-4 border-0">
                <h5 className="mb-3">Location Details</h5>
                {["address", "city", "state", "zipcode", "country"].map(
                  (field) => (
                    <div className="mb-3" key={field}>
                      <label className="form-label text-capitalize">
                        {field.replace("_", " ")}
                      </label>
                      <input
                        type="text"
                        name={field}
                        value={formik.values[field]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="form-control signin-input"
                        readOnly
                      />
                      {formik.touched[field] && formik.errors[field] && (
                        <div className="text-danger">
                          {formik.errors[field]}
                        </div>
                      )}
                    </div>
                  )
                )}
                <button
                  type="submit"
                  className="btn w-100 mt-3 sign-in-btn"
                  disabled={isloading}
                >
                  {isloading ? (
                    <ScaleLoader color="#fff" height={16} />
                  ) : (
                    "Save Address"
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </Modal.Body>

      <style jsx>{`
        .address-card:hover {
          background-color: #f8f9fa;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
        }
      `}</style>
    </Modal>
  );
};

export default AddressModal;
